// 디렉토리 경로: client/src/hooks/
// 파일명: useReferenceAnalysis.ts
// 파일 코드의 역할/설명: 레퍼런스 분석 프롬프트 복사, 결과 파싱, 인사이트 저장 흐름을 제공한다.

import { useCallback, useMemo, useState } from 'react'
import { LLM_SERVICES, type LLMServiceType } from '@/data/llm-prompts'
import { buildReferenceAnalysisPromptV2 } from '@/data/reference-analysis-prompts'
import { createText } from '@/services/firestore'
import { useAuth } from './useAuth'
import {
  ReferenceAnalysisParseError,
  makeReferenceAnalysisSummary,
  parseReferenceAnalysisInput,
} from '@/utils/referenceAnalysis'
import type {
  InsightRegisterKind,
  ReferenceAnalysisParseErrorCode,
  ReferenceAnalysisResult,
  ReferenceItem,
  SavedText,
} from '@/types'

export type ReferenceAnalysisStatus =
  | 'idle'
  | 'prompt_ready'
  | 'copy_success'
  | 'copy_failed'
  | 'parse_success'
  | 'parse_error'
  | 'saving'
  | 'save_success'
  | 'save_error'
  | 'auth_required'
  | 'popup_blocked'

interface UseReferenceAnalysisReturn {
  promptText: string
  rawResult: string
  parsedResult: ReferenceAnalysisResult | null
  status: ReferenceAnalysisStatus
  errorCode: ReferenceAnalysisParseErrorCode | null
  errorMessage: string | null
  isSaving: boolean
  llmServices: typeof LLM_SERVICES
  preparePrompt: (source: ReferenceItem) => string
  copyPrompt: (source: ReferenceItem) => Promise<boolean>
  openExternalLLM: (service: LLMServiceType) => boolean
  setRawResult: (value: string) => void
  parseResult: (rawText: string) => ReferenceAnalysisResult | null
  registerInsight: (
    kind: InsightRegisterKind,
    value: string,
    source: ReferenceItem,
    result: ReferenceAnalysisResult
  ) => Promise<SavedText | null>
  reset: () => void
}

async function copyTextWithFallback(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text)
    return true
  }

  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.setAttribute('readonly', 'true')
  textArea.style.position = 'fixed'
  textArea.style.left = '-9999px'
  textArea.style.top = '-9999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    return document.execCommand('copy')
  } finally {
    document.body.removeChild(textArea)
  }
}

function buildInsightPayload(
  kind: InsightRegisterKind,
  value: string,
  source: ReferenceItem,
  result: ReferenceAnalysisResult,
  userId: string
): Omit<SavedText, 'id' | 'createdAt' | 'updatedAt'> {
  const content = value.trim()
  const analysisSummary = makeReferenceAnalysisSummary(result)

  if (kind === 'structure') {
    return {
      content,
      title: `[구조] ${source.title}`,
      topic: source.title,
      source: source.title,
      charCount: content.length,
      type: 'reference',
      userId,
      referenceType: 'structure',
      analysisSummary,
    }
  }

  if (kind === 'idea') {
    return {
      content,
      title: `[아이디어] ${source.title}`,
      topic: source.title,
      source: source.title,
      charCount: content.length,
      type: 'reference',
      userId,
      referenceType: 'idea',
      analysisSummary,
    }
  }

  const topicContent = `주제: ${content}

활용 근거:
${analysisSummary.topFactors.map((factor) => `- ${factor}`).join('\n')}`

  return {
    content: topicContent,
    title: `[주제] ${content}`,
    topic: content,
    source: source.title,
    charCount: topicContent.length,
    type: 'reference',
    userId,
    referenceType: 'unspecified',
    analysisSummary,
  }
}

export function useReferenceAnalysis(): UseReferenceAnalysisReturn {
  const { user } = useAuth()
  const [promptText, setPromptText] = useState('')
  const [rawResult, setRawResult] = useState('')
  const [parsedResult, setParsedResult] =
    useState<ReferenceAnalysisResult | null>(null)
  const [status, setStatus] = useState<ReferenceAnalysisStatus>('idle')
  const [errorCode, setErrorCode] =
    useState<ReferenceAnalysisParseErrorCode | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const llmServices = useMemo(() => LLM_SERVICES, [])

  const preparePrompt = useCallback((source: ReferenceItem): string => {
    const prompt = buildReferenceAnalysisPromptV2(source)
    setPromptText(prompt)
    setStatus('prompt_ready')
    setErrorCode(null)
    setErrorMessage(null)
    return prompt
  }, [])

  const copyPrompt = useCallback(
    async (source: ReferenceItem): Promise<boolean> => {
      const prompt = preparePrompt(source)

      try {
        const copied = await copyTextWithFallback(prompt)
        setStatus(copied ? 'copy_success' : 'copy_failed')
        setErrorMessage(copied ? null : '브라우저가 자동 복사를 차단했습니다.')
        return copied
      } catch {
        setStatus('copy_failed')
        setErrorMessage('브라우저가 자동 복사를 차단했습니다.')
        return false
      }
    },
    [preparePrompt]
  )

  const openExternalLLM = useCallback((service: LLMServiceType): boolean => {
    const llmInfo = LLM_SERVICES[service]
    if (!llmInfo) return false

    const opened = window.open(llmInfo.url, '_blank', 'noopener,noreferrer')
    if (!opened) {
      setStatus('popup_blocked')
      setErrorMessage('팝업이 차단되었습니다. 링크를 직접 열어 주세요.')
      return false
    }

    return true
  }, [])

  const parseResult = useCallback(
    (rawText: string): ReferenceAnalysisResult | null => {
      try {
        const parsed = parseReferenceAnalysisInput(rawText)
        setParsedResult(parsed)
        setStatus('parse_success')
        setErrorCode(null)
        setErrorMessage(null)
        return parsed
      } catch (error) {
        const parseError =
          error instanceof ReferenceAnalysisParseError
            ? error
            : new ReferenceAnalysisParseError(
                'PARSE_INVALID_SCHEMA',
                '분석 결과 형식이 올바르지 않습니다.'
              )

        setStatus('parse_error')
        setErrorCode(parseError.code)
        setErrorMessage(parseError.message)
        return null
      }
    },
    []
  )

  const registerInsight = useCallback(
    async (
      kind: InsightRegisterKind,
      value: string,
      source: ReferenceItem,
      result: ReferenceAnalysisResult
    ): Promise<SavedText | null> => {
      if (!user) {
        setStatus('auth_required')
        setErrorMessage('저장하려면 로그인이 필요합니다.')
        return null
      }

      if (isSaving) return null

      const trimmed = value.trim()
      if (!trimmed) {
        setStatus('save_error')
        setErrorMessage('저장할 내용을 선택하거나 입력해 주세요.')
        return null
      }

      setIsSaving(true)
      setStatus('saving')
      setErrorMessage(null)

      try {
        const payload = buildInsightPayload(kind, trimmed, source, result, user.uid)
        const savedText = await createText(user.uid, payload)
        setStatus('save_success')
        return savedText
      } catch {
        setStatus('save_error')
        setErrorMessage('인사이트 저장에 실패했습니다.')
        return null
      } finally {
        setIsSaving(false)
      }
    },
    [isSaving, user]
  )

  const reset = useCallback(() => {
    setPromptText('')
    setRawResult('')
    setParsedResult(null)
    setStatus('idle')
    setErrorCode(null)
    setErrorMessage(null)
    setIsSaving(false)
  }, [])

  return {
    promptText,
    rawResult,
    parsedResult,
    status,
    errorCode,
    errorMessage,
    isSaving,
    llmServices,
    preparePrompt,
    copyPrompt,
    openExternalLLM,
    setRawResult,
    parseResult,
    registerInsight,
    reset,
  }
}

export default useReferenceAnalysis
